import {
  type AnimalFiltersEntity,
  type AnimalMetadataFilter,
  validateManageAnimalsSelection,
} from '@/components/Colony/Animals/Animals.utils';
import GroupLabel from '@/components/UI/GroupLabel/GroupLabel';
import Link from '@/components/UI/Link';
import NumericDelta from '@/components/UI/NumericDelta/NumericDelta';
import type { FilterOption, TanStackCellProps, TanStackColumnDef } from '@/components/UI/Table/TableComponent.model';
import { consumptionMetricIds } from '@/components/Workflow/Show/Views/Consumption/Consumption.utils';
import { FilterTypes as filterType } from '@/constants/FilterTypes';
import { isDead } from '@/constants/utils';
import { calculateAge, formatNumber, renderMetadataValue, successToast } from '@/helpers';
import { _isEmpty, _isNil, _isNotEmpty, _notNil } from '@/littledash';
import { AnimalSexMap, type AnimalSex } from '@/model/Animal.model';
import type { Animal, AnimalMetadata } from '@/model/Animal.model';
import type { LatestMeasurement } from '@/model/LatestMeasurements.model';
import type { CageApiId } from '@/model/Cage.model';
import type { ID } from '@/model/Common.model';
import type { MetadataItem } from '@/model/Metadata.model';
import type { PresetCalculation } from '@/model/PresetCalculation.model';
import type { BulkSamplesFormData } from '@/model/Sample.model';
import { type Study } from '@/model/Study.model';
import type { TreatmentGroup } from '@/model/TreatmentGroup.model';
import Http from '@/support/http';
import { web as webRoute } from '@/support/route';
import { RouteService } from '@/support/RouteService';
import { DateRenderFormat, DateUtils } from '@/utils/Date.utils';
import { modalAction } from '@/utils/modal';
import { type Dispatch } from 'redux';
import type { NavigateFunction } from 'react-router-dom';
import BenchlingEntityLink from '@/components/UI/BenchlingEntityLink/BenchlingEntityLink';
import AnimalAlerts from './AnimalAlerts';
import LatestObservationsCell from './LatestObservationsCell';
import { containsOperation, notContainsOperation, selectOperations } from '@/utils/FiltersOperations.utils.ts';

export const animalTableColumns = (
  studyId: ID,
  calculations: Array<PresetCalculation>,
  changePercentage: string,
  metadataOptions: Array<AnimalMetadataFilter>
): Array<TanStackColumnDef<Animal>> => {
  const calculationColumns = studyCalculationColumns(calculations, changePercentage);
  const metadataColumns = animalMetadataColumns(metadataOptions);

  return [
    {
      id: 'number',
      header: 'No.',
      size: 75,
      accessorKey: 'number',
      isVisible: true,
    },
    {
      id: 'catalog',
      header: 'Animal No.',
      accessorKey: 'catalog',
      isVisible: false,
    },
    {
      id: 'benchling_entity_id',
      header: 'Registry API ID',
      accessorKey: 'benchling_entity_id',
      isVisible: false,
      cell: (props: TanStackCellProps<Animal>) => props.row.original.benchling_entity_id ?? '-',
    },
    {
      id: 'name',
      header: 'Name',
      accessorKey: 'name',
      isVisible: true,
      cell: (props: TanStackCellProps<Animal>) => {
        const { row, table } = props;
        const fetchTableData = (table?.options?.meta as any)?.apiTable?.fetchTableData;
        const { id: animalId, api_id: animalApiId, name, alerts, cage, benchling_entity_web_url } = row.original;
        return (
          <div className="flex justify-between items-center w-100">
            <div className="flex items-center">
              <BenchlingEntityLink
                entityType="animals"
                apiId={animalApiId ?? ''}
                benchlingEntityWebUrl={benchling_entity_web_url}
                visible={row.original.synced_to_benchling === true}
              />
              <Link
                to={webRoute('animals.show', {
                  id: studyId,
                  subject_id: animalId,
                })}
                className="link blue"
              >
                {name}
              </Link>
            </div>
            {!_isEmpty(alerts) && _notNil(animalApiId) && (
              <AnimalAlerts
                animalName={name}
                cageName={cage?.name}
                animalApiId={animalApiId}
                alerts={alerts ?? []}
                reloadAlerts={() => fetchTableData()}
              />
            )}
          </div>
        );
      },
    },
    {
      id: 'tail',
      header: 'Tail ID',
      accessorFn: (row: Animal) => row.alt_ids?.tail,
      isVisible: false,
    },
    {
      id: 'donor',
      header: 'Donor ID',
      accessorFn: (row: Animal) => row.alt_ids?.donor,
      isVisible: false,
    },
    {
      id: 'ear',
      header: 'Ear ID',
      accessorFn: (row: Animal) => row.alt_ids?.ear,
      isVisible: false,
    },
    {
      id: 'tag',
      header: 'Tag ID',
      accessorFn: (row: Animal) => row.alt_ids?.tag,
      isVisible: false,
    },
    {
      id: 'cage',
      header: 'Cage',
      accessorFn: (row: Animal) => row.cage?.name,
      isVisible: true,
      // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
      cell: ({ row }: { row: { original: Animal } }) => isDead(row.original) || '-',
    },
    ...calculationColumns,
    {
      id: 'latest_observed_at',
      header: 'Latest observed date',
      accessorKey: 'latest_observed_at',
      isVisible: false,
      cell: ({ row }: { row: { original: Animal } }) => (
        <>
          {DateUtils.renderDateTime(row.original.latest_observed_at, {
            format: DateRenderFormat.Date,
            defaultResponse: '',
          })}
        </>
      ),
    },
    {
      id: 'latest_observations',
      header: 'Latest observations',
      accessorKey: 'latest_observations',
      size: 250,
      isVisible: false,
      enableSorting: false,
      cell: ({ row }: { row: { original: Animal } }) =>
        _notNil(row.original.api_id) ? (
          <LatestObservationsCell
            animalApiId={row.original.api_id}
            animalName={row.original.name ?? null}
            latestObservations={row.original.latest_observations}
            latestObservedAt={row.original.latest_observed_at}
          />
        ) : null,
    },
    {
      id: 'study_group',
      header: 'Study group',
      accessorFn: (row: Animal) => row.study_group?.no,
      isVisible: true,
      size: 200,
      cell: ({ row }: { row: { original: Animal } }) =>
        _notNil(row.original.study_group) ? <GroupLabel group={row.original.study_group} /> : '-',
    },
    {
      id: 'tracking_started_at',
      header: 'Tracking date',
      accessorKey: 'tracking_started_at',
      isVisible: false,
      cell: ({ row }: { row: { original: Animal } }) => (
        <>{DateUtils.renderDate(row.original.tracking_started_at, { defaultResponse: '' })}</>
      ),
    },
    {
      id: 'disease_inducted_at',
      header: 'Disease induction date',
      accessorKey: 'disease_inducted_at',
      isVisible: false,
      cell: ({ row }: { row: { original: Animal } }) => (
        <>{DateUtils.renderDate(row.original.disease_inducted_at, { defaultResponse: '' })}</>
      ),
    },
    {
      id: 'sex',
      header: 'Sex',
      accessorKey: 'sex',
      enableSorting: true,
      isVisible: false,
      size: 75,
      cell: ({ row }: { row: { original: Animal } }) => AnimalSexMap[row.original.sex as AnimalSex] ?? '-',
    },
    {
      id: 'dob',
      header: 'DOB',
      accessorKey: 'dob',
      isVisible: false,
      cell: ({ row }: { row: { original: Animal } }) => (
        <>{DateUtils.renderDate(row.original.dob, { defaultResponse: '-' })}</>
      ),
    },
    {
      id: 'age',
      header: 'Age',
      accessorFn: (row: Animal) => row.dob,
      isVisible: true,
      enableSorting: false,
      cell: ({ row }: { row: { original: Animal } }) =>
        _notNil(row.original.dob) ? calculateAge(row.original.dob, row.original.terminated_at) : '-',
    },
    {
      id: 'species',
      header: 'Species',
      accessorFn: (row: Animal) => row.species_name,
      isVisible: false,
      enableSorting: false,
    },
    {
      id: 'strain',
      header: 'Strain',
      accessorFn: (row: Animal) => row.strain_name,
      isVisible: false,
      enableSorting: false,
    },
    ...metadataColumns,
  ];
};

const percentageChangeFieldMap: Record<string, keyof LatestMeasurement> = {
  value_change_from_first: 'value_change_from_first',
  value_change_from_tracking_date: 'value_change_from_tracking_date',
  value_change_from_disease_induction_date: 'value_change_from_disease_induction_date',
};

export const studyCalculationColumns = (
  calculations: Array<PresetCalculation>,
  changePercentage: string
): Array<TanStackColumnDef<Animal>> =>
  calculations.map(({ id, name, unit }: PresetCalculation) => ({
    id,
    accessorFn: (row: Animal) => row.latestMeasurement?.[id]?.value,
    // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
    header: `${name} ${unit !== null && unit ? ` (${unit})` : ''}`,
    isVisible: id === 'weight' || id === 'tumour_volume',
    size: 250,
    cell: ({ row }: TanStackCellProps<Animal>) => {
      const measurement = row.original.latestMeasurement?.[id];
      if (_notNil(measurement)) {
        const { value, value_change: valueChange } = measurement;

        const percentageField = percentageChangeFieldMap[changePercentage];
        const valueChangeToUse = _notNil(percentageField) ? measurement[percentageField] : valueChange;

        const hidePercentageChange = _isNil(valueChangeToUse) || valueChangeToUse === 0;

        return (
          <>
            {formatNumber(value)}
            <NumericDelta
              value={value}
              valueChange={valueChangeToUse}
              className="f7"
              percentageOnly={true}
              hidePercentageChange={hidePercentageChange}
            />
          </>
        );
      }
      return '-';
    },
  }));

export const animalMetadataColumns = (
  metadataOptions?: Array<AnimalMetadataFilter>
): Array<TanStackColumnDef<Animal>> =>
  metadataOptions?.map(({ id, name, slug }) => ({
    id: `meta_${slug}`,
    header: name,
    isVisible: false,
    enableSorting: false,
    cell: ({ row }: { row: { original: { metadata: Array<AnimalMetadata> } } }) => {
      const meta = row.original.metadata.find(({ glossary_id }: AnimalMetadata) => glossary_id === id);
      return _notNil(meta) ? renderMetadataValue(meta) : '';
    },
  })) ?? [];

export const animalBulkActions = (
  selectedFlatRows: Array<{ original: Animal }>,
  refetchTableData: () => Promise<void>,
  study: Study,
  dispatch: Dispatch,
  navigate: NavigateFunction,
  atunesIntegrationEnabled: boolean,
  inReview: boolean = false
) => {
  const {
    study_groups: groups,
    id: studyId,
    settings: { calculations },
  } = study;
  const consumptionEnabled = calculations.some((calculation) => consumptionMetricIds.has(calculation.id));

  const { openModal, closeModal } = modalAction(dispatch);

  const handleCallback = async (text: string) => {
    closeModal();
    await refetchTableData();
    // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
    if (text) {
      successToast(text);
    }
  };

  const animals = selectedFlatRows.map((row: { original: Animal }) => row.original);

  const sectionOne = [];
  const sectionTwo = [];

  // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
  const terminatedAnimals = animals.some((subject: Animal) => subject.terminated_at || _isNil(subject.cage));
  sectionOne.push({
    name: 'Assign identifiers',
    tooltip: 'All selected animals must be alive and in a cage to assign identifiers.',
    disabled: terminatedAnimals,
    action: () => {
      return openModal('ASSIGN_IDENTIFIERS', {
        // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
        animals: animals.filter((animal: Animal) => !animal.terminated_at),
        studyApiId: study.api_id,
        handleCallback,
      });
    },
  });

  if (study.settings?.id !== null) {
    sectionOne.push({
      name: 'Randomize to groups',
      disabled: inReview,
      tooltip: 'Randomization is not allowed while the study is in review.',
      action: () => {
        dispatch({ type: 'SET_SELECTED_SUBJECTS', selectedSubjects: animals });
        navigate(webRoute('studies.randomization', { id: study.id }));
      },
    });
  }

  const canAddToGroup = (selectedSubjects: Array<Animal>, groups: Array<TreatmentGroup>) =>
    // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
    !(selectedSubjects.some((subject) => subject.study_group || subject.terminated_at) || _isEmpty(groups));
  sectionOne.push({
    name: 'Add to group (manual)',
    disabled: !canAddToGroup(animals, groups),
    tooltip: _isEmpty(groups)
      ? 'Study groups have not yet been created.'
      : 'One or more of the selected animals is already in a group or deceased.',
    action: () => {
      openModal('ASSIGN_SUBJECT_TO_GROUP', {
        subject: animals,
        closeModal,
        handleCallback,
      });
    },
  });

  // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
  if (animals.some((subject: Animal) => subject?.study_group)) {
    sectionTwo.push({
      name: 'Remove from group',
      action: () =>
        openModal('REMOVE_SUBJECT_FROM_GROUP', {
          subject: animals,
          closeModal,
          handleCallback,
        }),
    });
  }

  sectionOne.push({
    name: 'Set tracking date',
    action: () =>
      openModal('SET_TRACKING_DATE', {
        subjects: animals,
        studyApiId: study.api_id,
        closeModal,
        handleCallback,
      }),
  });

  sectionOne.push({
    name: 'Set disease induction date',
    action: () => {
      openModal('UPDATE_DISEASE_INDUCTION_DATE', {
        studyApiId: study.api_id,
        subjects: animals,
        handleCallback,
      });
    },
  });

  sectionOne.push(
    {
      name: 'Update sex',
      action: () =>
        openModal('UPDATE_SEX', {
          subjects: animals,
          studyApiId: study.api_id,
          closeModal,
          handleCallback,
        }),
    },
    {
      name: 'Update DOB',
      action: () =>
        openModal('UPDATE_DOB', {
          subjects: animals,
          studyApiId: study.api_id,
          closeModal,
          handleCallback,
        }),
    }
  );

  // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
  const canRecage = (selectedSubjects: Array<Animal>) => !selectedSubjects.some((subject) => subject.terminated_at);
  sectionOne.push({
    name: 'Recage',
    disabled: !canRecage(animals) || inReview,
    tooltip: 'All selected animals must be alive in order to perform recaging.',
    action: () => {
      dispatch({ type: 'SET_SELECTED_SUBJECTS', selectedSubjects: animals });
      navigate(webRoute('studies.recaging', { studyId: study.id }));
    },
  });

  const canMoveToStudy = (selectedSubjects: Array<Animal>) =>
    selectedSubjects.some((subject) => _isNotEmpty(subject.cage_id));

  sectionOne.push({
    name: 'Move to another study',
    disabled: !canMoveToStudy(animals),
    tooltip: 'Selected animals must be alive to move to another study.',
    action: () => {
      openMoveToStudy();
    },
  });

  const openMoveToStudy = () =>
    openModal('MOVE_TO_STUDY', {
      subjects: animals,
      handleCallback: openMoveToStudySelect,
    });

  const openMoveToStudySelect = (cageApiIds: Array<CageApiId>, currentStudy: Study, animalCount: number) =>
    openModal('MOVE_TO_STUDY_SELECT_STUDY', {
      cageApiIds,
      currentStudy,
      animalCount,
      animals,
      handleCallback: openMoveToStudyConfirmation,
      moveToStudyConfirmationV2Callback: openMoveToStudyConfirmationV2,
    });

  const openMoveToStudyConfirmationV2 = (
    targetStudy: Study | undefined,
    sourceStudy: Study,
    animal_api_ids: Array<string>,
    animalCageNames: Set<string>
  ) => {
    openModal('MOVE_TO_STUDY_CONFIRMATION_V2', {
      targetStudy,
      sourceStudy,
      animal_api_ids,
      animalCageNames,
      handleCallback,
    });
  };

  const openMoveToStudyConfirmation = (
    cageApiIds: Array<CageApiId>,
    currentStudy: Study,
    selectedStudy: Study,
    animalCount: number
  ) => {
    openModal('MOVE_TO_STUDY_CONFIRMATION', {
      cageApiIds,
      currentStudy,
      selectedStudy,
      animalCount,
      handleCallback: openMoveToStudySuccess,
    });
  };

  const openMoveToStudySuccess = (currentStudy: Study, newStudy: Study, animalCount: number) =>
    openModal('MOVE_TO_STUDY_SUCCESS', {
      currentStudy,
      newStudy,
      animalCount,
      handleCallback,
    });

  const openCollectSamples = () =>
    openModal('COLLECT_SAMPLES', {
      handleCallback: viewSampleDetails,
    });

  const viewSampleDetails = (sampleDetails: BulkSamplesFormData, sampleMetadata: Array<MetadataItem>) => {
    openModal('VIEW_SAMPLE_DETAILS', {
      studyId,
      subjects: animals,
      sampleDetails,
      metadata: sampleMetadata,
      handleCallback: (finished: boolean, viewSamples: boolean) => {
        closeModal();
        if (finished) {
          navigate(webRoute(`studies.${viewSamples ? 'samples' : 'animals'}`, { id: study.id }));
        } else {
          openCollectSamples();
        }
      },
    });
  };

  sectionOne.push({
    name: 'Collect samples',
    disabled: inReview,
    tooltip: 'Collecting samples is not allowed while the study is in review.',
    action: () => {
      openCollectSamples();
    },
  });

  sectionOne.push({
    name: 'Record observations',
    disabled: inReview,
    tooltip: inReview ? 'Recording observations is not allowed while the study is in review.' : undefined,
    action: () =>
      openModal('BULK_RECORD_OBSERVATIONS', {
        animals: animals,
        studyApiId: study.api_id,
        closeModal,
        handleCallback,
      }),
  });

  const defaultSelectionProps = {
    hasWritePermission: false,
    isNotDeceased: true,
    hasActiveStudy: false,
    hasIdenticalStudy: false,
  };

  sectionOne.push({
    name: 'Edit Animal details',
    key: 'edit_details',
    ...validateManageAnimalsSelection(defaultSelectionProps, animals, 1000, false),
    action: () => {
      navigate(webRoute('studies.animals.edit', { id: studyId }), {
        state: { animals },
      });
    },
  });

  if (!atunesIntegrationEnabled) {
    if (animals.every((subject: Animal) => subject?.terminated_at != null)) {
      sectionTwo.push({
        name: 'Unmark as deceased',
        action: () =>
          openModal('UNDO_EUTHANISE_SUBJECT', {
            subjects: animals,
            closeModal,
            handleCallback,
          }),
      });
    } else {
      sectionTwo.push({
        name: 'Mark as deceased',
        ...validateManageAnimalsSelection(defaultSelectionProps, animals, undefined, inReview),
        action: () =>
          openModal('BULK_EUTHANISE_SUBJECTS', {
            subjects: animals,
            closeModal,
            handleCallback,
            consumptionEnabled: consumptionEnabled,
          }),
      });
    }
  } else {
    const allSameInstance = animals.every(
      (animal: Animal) => animal.external_instance_id === animals[0]?.external_instance_id
    );
    // Animals with external_batch_id are experimental, without are breeding
    const isExperimentalAnimal = (animal: Animal) => _notNil(animal.external_batch_id);
    const allExperimental = animals.every(isExperimentalAnimal);
    const allBreeding = animals.every((animal: Animal) => !isExperimentalAnimal(animal));
    const hasMixedAnimalTypes = !allExperimental && !allBreeding;

    const singleAnimalUrl = animals[0]?.external_termination_url;
    // Determine action based on animal type and count:
    // - Single animal (breeding or experimental): ReductionSingle
    // - Multiple breeding animals: ReductionMulti
    // - Multiple experimental animals: Reduction
    const getAction = () => {
      if (animals.length === 1) {
        return 'ReductionSingle';
      }
      return allExperimental ? 'Reduction' : 'ReductionMulti';
    };
    const baseUrl = singleAnimalUrl?.replace(/action=.*?&/, `action=${getAction()}&`);
    // For experimental animals, use external_batch_id; for breeding animals, use external_id
    const ids = animals
      .map((animal: Animal) => (isExperimentalAnimal(animal) ? animal.external_batch_id : animal.external_id))
      .join(',');
    const finalUrl = baseUrl?.replace(/id=\d+/, `id=${ids}`);
    // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
    const urlTooLong = finalUrl && finalUrl.length > 2048;
    const isDisabled = (selectedSubjects: Array<Animal>) =>
      // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
      selectedSubjects.some((subject: Animal) => subject.terminated_at || _isNil(subject.cage));

    sectionTwo.push({
      name: 'tick@lab exit',
      ...validateManageAnimalsSelection({ ...defaultSelectionProps, isNotDeceased: false }, animals),
      disabled: isDisabled(animals) || !allSameInstance || hasMixedAnimalTypes || urlTooLong,
      tooltip: isDisabled(animals)
        ? 'All selected animals must be alive and in a cage to exit in tick@lab'
        : !allSameInstance
          ? 'Selected animals must be from the same tick@lab instance'
          : hasMixedAnimalTypes
            ? 'Cannot mix breeding and experimental animals'
            : // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
              urlTooLong
              ? 'Too many animals selected'
              : undefined,
      action: () => {
        // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
        if (!finalUrl) {
          return;
        }
        window.open(finalUrl, '_blank');
      },
    });
  }
  if (!atunesIntegrationEnabled) {
    const handleDelete = async (reasonForDelete?: string) => {
      try {
        const url = RouteService.api({
          endpoint: 'DELETE /api/v1/studies/{studyApiId}/animals/destroy-many',
          path: { studyApiId: study.api_id },
        }).url.href;
        await Http.delete(url, {
          data: {
            animal_api_ids: animals.map((s: Animal) => s.api_id),
            reason_for_change: reasonForDelete,
          },
        });
        return handleCallback('Successfully deleted!');
      } catch {
        successToast('There was a problem deleting animals, please try again later.');
      }
    };

    sectionTwo.push({
      name: 'Delete',
      action: () =>
        openModal('CONFIRM_DELETE_SUBJECTS', {
          subject: animals,
          closeModal,
          onClick: handleDelete,
        }),
      className: 'red',
    });
  }

  return [sectionOne, sectionTwo];
};

export const animalFilterOptions = (
  calculations: Array<PresetCalculation>,
  studyGroups: Array<TreatmentGroup> | undefined,
  studyId: ID,
  observationOptions: Array<string> = [],
  animalFilters: AnimalFiltersEntity | Array<AnimalMetadataFilter>
): Array<FilterOption> => {
  const filters: Array<FilterOption> = [
    ...calculations.map(({ id, name }) => ({
      name,
      value: id,
      type: 'measurements',
      operations: [
        {
          name: 'greater than',
          value: filterType.gt,
        },
        {
          name: 'less than',
          value: filterType.lt,
        },
      ],
    })),
    {
      value: 'cage',
      name: 'Cage name',
      operations: [
        {
          name: 'is equal to',
          value: filterType.eq,
          options: [],
        },
        {
          name: 'is not equal to',
          value: filterType.ne,
          options: [],
        },
        {
          name: 'contains',
          value: filterType.contains,
        },
        {
          name: 'does not contain',
          value: filterType.not_contains,
        },
      ],
      fetchFromApi: {
        placeholder: 'Search cages',
        url: 'studies.cages.list',
        params: {
          studyId,
        },
        queryParams: {
          sort: 'cage_order',
          order: 'asc',
          perPage: -1,
        },
      },
    },
    {
      value: 'deceased',
      name: 'Living status',
      operations: [
        {
          name: 'is equal to',
          value: filterType.eq,
          options: [{ name: 'Alive', value: 'null' }],
        },
        {
          name: 'is not equal to',
          value: filterType.ne,
          options: [{ name: 'Alive', value: 'null' }],
        },
      ],
    },
    {
      value: 'sex',
      name: 'Sex',
      operations: [
        {
          name: 'is equal to',
          value: filterType.eq,
          options: [
            { value: 'm', name: 'Male' },
            { value: 'f', name: 'Female' },
            { value: 'u', name: 'Unknown' },
          ],
        },
      ],
    },
    {
      value: 'alerts',
      name: 'Alert',
      operations: [
        {
          name: 'is',
          value: filterType.eq,
          options: [
            { value: 'alert_unresolved', name: 'Unresolved' },
            { value: 'alert_resolved', name: 'Resolved' },
          ],
        },
      ],
    },
    ...(_isNotEmpty(observationOptions)
      ? [
          {
            value: 'observation',
            name: 'Observation',
            operations: [
              {
                name: 'is equal to',
                value: filterType.eq,
                options: observationOptions.map((value) => ({ value, name: value })),
              },
              {
                name: 'is not equal to',
                value: filterType.ne,
                options: observationOptions.map((value) => ({ value, name: value })),
              },
              {
                name: filterType.IS_NULL,
                value: filterType.IS_NULL,
              },
              {
                name: filterType.IS_NOT_NULL,
                value: filterType.IS_NOT_NULL,
              },
            ],
          },
        ]
      : []),
  ];

  if (_isNotEmpty(studyGroups)) {
    filters.push({
      value: 'study_group',
      name: 'Study Group',
      operations: [
        {
          name: 'is equal to',
          value: filterType.eq,
          options: [],
        },
        {
          name: 'is not equal to',
          value: filterType.ne,
          options: [],
        },
        {
          name: filterType.IS_NULL,
          value: filterType.IS_NULL,
        },
        {
          name: filterType.IS_NOT_NULL,
          value: filterType.IS_NOT_NULL,
        },
      ],
      fetchFromApi: {
        placeholder: 'Search groups',
        url: 'studies.groups.list',
        params: {
          id: studyId,
        },
        queryParams: {
          perPage: -1,
        },
      },
    });
  }

  if (_isNotEmpty(animalFilters)) {
    // Handle both AnimalFiltersEntity and Array<AnimalMetadataFilter>
    const filtersEntity = Array.isArray(animalFilters) ? { metadata: animalFilters, fields: [] } : animalFilters;
    filters.push(...addAnimalFilters(filtersEntity));
  }

  return filters;
};

export const addAnimalFilters = (filters: AnimalFiltersEntity): Array<FilterOption> => {
  const metadata =
    filters?.metadata?.map((item: AnimalMetadataFilter) => {
      switch (item.field_type) {
        case 'date':
          return {
            value: item.id,
            name: item.name,
            operations: [
              {
                name: filterType.contains,
                value: filterType.eq_date,
              },
              {
                name: filterType.not_contains,
                value: filterType.not_eq_date,
              },
            ],
          };
        case 'multi_select': {
          return {
            value: item.id,
            name: item.name,
            operations: selectOperations,
            fetchFromApi: {
              placeholder: 'Multiselect options',
              url: 'GET /api/v1/metadata-glossary/{metadataGlossaryId}/used-options',
              params: { metadataGlossaryId: item.id },
              fieldType: 'multi_select',
              useModernApi: true,
            },
          };
        }
        case 'select': {
          return {
            value: item.id,
            name: item.name,
            operations: selectOperations,
            fetchFromApi: {
              placeholder: 'Select options',
              url: 'GET /api/v1/metadata-glossary/{metadataGlossaryId}/used-options',
              params: { metadataGlossaryId: item.id },
              fieldType: 'select',
              useModernApi: true,
            },
          };
        }

        default:
          return {
            value: item.id,
            name: item.name,
            operations: [...containsOperation, ...notContainsOperation],
          };
      }
    }) ?? [];

  const strainsAndSpecies =
    filters?.fields?.reduce((acc: Array<FilterOption>, field) => {
      if (field.name === 'Strain' || field.name === 'Species') {
        acc.push({
          value: field.value,
          name: field.name,
          operations: [
            {
              name: filterType.eq,
              value: filterType.eq,
              options: field.options,
            },
            {
              name: filterType.ne,
              value: filterType.ne,
              options: field.options,
            },
          ],
        });
      }
      return acc;
    }, []) ?? [];

  return [...metadata, ...strainsAndSpecies];
};
